import { createStore, createEffect, createEvent, combine } from 'effector';
import { fetchAndActivate, getAll, getValue, Value } from 'firebase/remote-config';
import i18n from 'i18n';
import { ApiUtils } from '@shared/utils';
import { RequestStrategyEnum } from '@shared/api';
import { remoteConfig } from '../../../firebase.config';

enum ValueEnum {
  Boolean = 'boolean',
  Number = 'number',
  JSON = 'json',
  String = 'string',
}
type ValueType = ValueEnum | `${ValueEnum}`;

const getRemoteConfigEffect = createEffect(async () => {
  await fetchAndActivate(remoteConfig);
  return getAll(remoteConfig);
});
function getValueEvent(key: string, type: ValueEnum.String | 'string'): string;
function getValueEvent(key: string, type: ValueEnum.Number | 'number'): number;
function getValueEvent(key: string, type: ValueEnum.Boolean | 'boolean'): boolean;
function getValueEvent<T = {}>(key: string, type: ValueEnum.JSON | 'json'): T;
function getValueEvent(key: string): string;
function getValueEvent(key: string, type?: ValueType) {
  const value: Value = getValue(remoteConfig, key);

  switch (type) {
    case ValueEnum.String:
      return value.asString();
    case ValueEnum.Number:
      return value.asNumber();
    case ValueEnum.Boolean:
      return value.asBoolean();
    case ValueEnum.JSON:
      return JSON.parse(value.asString() || '{}');
    default:
      return value.asString();
  }
}

const clearEvent = createEvent();

const $dataStore = createStore<Record<string, Value>>({})
  .on(getRemoteConfigEffect.done, (current, data) => data.result || current)
  .reset(clearEvent);

const $loadingStore = createStore<boolean>(false)
  .on(getRemoteConfigEffect.pending, (_, data) => data)
  .on(getRemoteConfigEffect.fail, () => false)
  .on(getRemoteConfigEffect.done, () => false)
  .reset(clearEvent);

const $errorStore = createStore<string | null>(null)
  .on(getRemoteConfigEffect, () => null)
  .on(getRemoteConfigEffect.fail, (_, data) => i18n.t(`errors.api.${data.error.message}`, data.error.message))
  .on(getRemoteConfigEffect.done, () => null)
  .reset(clearEvent);

export const $remoteConfigStore = {
  events: {
    getRemoteConfig: ApiUtils.wrapperApi(getRemoteConfigEffect, RequestStrategyEnum.Debounce),
    getValue: getValueEvent,
    clear: clearEvent,
  },
  store: combine({
    loading: $loadingStore,
    data: $dataStore,
    error: $errorStore,
  }),
};
